import { useEffect } from "react";
import {
  ApplicationRouter,
  ProtectedRoute,
  Route,
} from "@aviv-unified-frontend/runtime/router";
import { createUseAuthenticationGuard } from "@aviv-unified-frontend/runtime/authentication";

import { NotFoundErrorPage } from "../../pages/errors";
import { Loading } from "../Loading";
import { BRAND_CONFIGURATION } from "../../../configs/brands";
import { useGlobalStore } from "../../utils/useGlobalStore/useGlobalStore";
import { ClientSuspense } from "../ClientSuspense";

import { createRoutes } from "./createRoutes";
import type { RouteProps } from "./types";

type RouteElementProps = Pick<
  RouteProps,
  "id" | "authorization" | "RemoteComponent"
>;

const RouteElement = ({
  id,
  authorization,
  RemoteComponent,
}: RouteElementProps) => {
  const { initialApplication, setApplicationId } = useGlobalStore();

  useEffect(() => {
    /**
     * <ScrollRestoration /> only works if using a data router
     * This is a native solution to scroll to the top of the page
     *
     * @see {@link https://reactrouter.com/en/main/components/scroll-restoration ScrollRestoration}
     */
    window.scrollTo({ top: 0, behavior: "auto" });
    setApplicationId(id);
  }, [id, setApplicationId]);

  const isPrerenderedApplication = initialApplication?.id === id;

  const isProtected =
    Boolean(BRAND_CONFIGURATION.authentication) &&
    authorization === "authenticated";

  const Component = isPrerenderedApplication ? (
    <initialApplication.Component />
  ) : (
    <ClientSuspense>
      <RemoteComponent />
    </ClientSuspense>
  );

  return (
    <ProtectedRoute
      isProtected={isProtected}
      useGuards={createUseAuthenticationGuard({
        shouldRedirect: isProtected,
      })}
      ErrorComponent={NotFoundErrorPage}
      LoadingComponent={Loading}
    >
      {Component}
    </ProtectedRoute>
  );
};

const ROUTES = createRoutes();

export const Router = () => {
  return (
    <ApplicationRouter>
      {ROUTES.map(({ id, path, RemoteComponent, authorization }) => (
        <Route
          key={`${id}-${path}`}
          path={path}
          element={
            <RouteElement
              key={`${id}-${path}`}
              id={id}
              authorization={authorization}
              RemoteComponent={RemoteComponent}
            />
          }
        />
      ))}
      <Route
        path="*"
        element={<NotFoundErrorPage />}
      />
    </ApplicationRouter>
  );
};
